import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Loader2 } from 'lucide-react';
import AddFamilyMemberForm from './AddFamilyMemberForm';
import FamilyMemberList from './FamilyMemberList';
import FamilyPlanMemberStatus from './FamilyPlanMemberStatus';
import { familyPlanService, FamilyPlanStatus } from '../../services/familyPlanService';

const FamilyPlanPage: React.FC = () => {
  const [status, setStatus] = useState<FamilyPlanStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await familyPlanService.getStatus();
      setStatus(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load family plan status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = async (email: string) => {
    setIsAdding(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await familyPlanService.addMember(email);
      setSuccessMessage(response.message);
      await loadStatus();
    } catch (err: any) {
      throw err; // Let the form handle the error display
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (memberId: number) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    setIsRemoving(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await familyPlanService.removeMember(memberId);
      setSuccessMessage(response.message);
      await loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleLeave = async () => {
    await loadStatus();
    window.location.href = '/'; // Redirect to home after leaving
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!status || (!status.is_owner && !status.is_member)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert>
          <AlertDescription>
            You do not have an active family plan. Subscribe to the Family tier to get started.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">
          Family Plan
        </h1>
        <p className="text-muted-foreground mt-2">
          {status.is_owner
            ? 'Manage your family members and subscription'
            : 'View your family plan details'}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {successMessage && (
        <Alert className="mb-4 bg-green-50 border-green-200">
          <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
        </Alert>
      )}

      {status.is_owner && (
        <>
          {/* Owner Overview */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>
                Plan Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant={status.plan_status === 'active' ? 'default' : 'secondary'}>
                  {status.plan_status || 'Unknown'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Members:</span>
                <span className="font-medium">
                  {status.current_member_count} / {status.max_members}
                </span>
              </div>
              {status.subscription_end_date && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Next Billing Date:</span>
                  <span className="font-medium">
                    {new Date(status.subscription_end_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Subscription Status:</span>
                <Badge variant={status.subscription_status === 'active' ? 'default' : 'secondary'}>
                  {status.subscription_status}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Add Member Form */}
          <div className="mb-6">
            <AddFamilyMemberForm
              onAdd={handleAddMember}
              isAdding={isAdding}
              currentCount={status.current_member_count}
              maxMembers={status.max_members}
            />
          </div>

          {/* Member List */}
          <FamilyMemberList
            members={status.members}
            onRemoveMember={handleRemoveMember}
            isRemoving={isRemoving}
          />
        </>
      )}

      {status.is_member && !status.is_owner && (
        <FamilyPlanMemberStatus
          ownerEmail={status.owner_email}
          subscriptionEndDate={status.subscription_end_date}
          onLeave={handleLeave}
        />
      )}
    </div>
  );
};

export default FamilyPlanPage;
